import { useEffect, useMemo, useRef, useState } from "react";
import { LYRICS_MAX_CHARS, LYRICS_MAX_CHARS_LONG, type WorkResponse } from "@studio/shared";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { loadDraftBackup, useAutosave } from "../lib/autosave";
import { onDraftArrival } from "../lib/lyricsDraft";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";

interface DraftResponse {
  draft: string;
  provider: string;
}

export function LyricsPage({ workId }: { workId: string }) {
  const query = useWork(workId);
  if (query.loading) return <LoadingPage />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "가사를 불러오지 못했습니다."} />;
  }
  return <LyricsEditor workId={workId} initial={query.data} />;
}

function LyricsEditor({ workId, initial }: { workId: string; initial: WorkResponse }) {
  const backup = loadDraftBackup(workId);
  const [topic, setTopic] = useState(backup?.topic ?? initial.work.topic ?? "");
  const [lyrics, setLyrics] = useState(backup?.lyrics ?? initial.work.lyrics ?? "");
  const [drafting, setDrafting] = useState(false);
  const [provider, setProvider] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 🚨 기다리는 동안 학생이 가사를 고쳤으면 늦게 온 초안은 덮지 않고 여기 둔다(P0-2).
  const [offeredDraft, setOfferedDraft] = useState<string | null>(null);
  const lyricsRef = useRef(lyrics);
  lyricsRef.current = lyrics;
  // 다음·이전으로 옮기는 저장이 진행 중인가 — 그동안 도착한 초안은 버린다(옮길 때의 가사가 저장된다).
  const movingRef = useRef(false);
  const payload = useMemo(() => ({
    step: "lyrics" as const,
    mode: "lyrics" as const,
    topic,
    lyrics,
  }), [lyrics, topic]);
  const autosave = useAutosave(workId, payload);
  const dirty = topic !== (initial.work.topic ?? "") || lyrics !== (initial.work.lyrics ?? "");

  /**
   * 주제 칸은 내용을 따라 늘어난다(2026-08-07 QA).
   *
   * 한 줄짜리 `<input>` 이라 "운동회에서 마지막 계주로 역전한 날" 같은 평범한 주제도
   * 앞부분이 잘려 보였다 — 좁은 왼쪽 패널 안이라 더 심했다. 학생이 자기가 뭘 썼는지
   * 못 보는 칸이었다. 마운트 시에도 한 번 맞춘다(이어보기로 복원된 긴 주제).
   */
  const topicRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const field = topicRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  }, [topic]);

  const requestDraft = async (): Promise<void> => {
    if (!topic.trim()) {
      setError("먼저 노래 주제를 적어 주세요.");
      return;
    }
    setDrafting(true);
    setError(null);
    setOfferedDraft(null);
    const lyricsAtRequest = lyricsRef.current;
    try {
      const result = await apiRequest<DraftResponse>("/api/ai/lyrics-draft", {
        method: "POST",
        body: JSON.stringify({ topic: topic.trim() }),
      });
      const arrival = onDraftArrival(lyricsAtRequest, lyricsRef.current, result.draft);
      if (movingRef.current) {
        // 학생이 이미 내 가사로 넘어가기로 했다 — 지금 바꾸면 저장되지 않고 사라진다(Codex 재검수 P1).
      } else if (arrival.kind === "apply") setLyrics(arrival.lyrics);
      else setOfferedDraft(arrival.draft);
      setProvider(result.provider);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "가사 초안을 받지 못했습니다.");
    } finally {
      setDrafting(false);
    }
  };

  const move = async (step: "mode" | "style"): Promise<void> => {
    // 이동 저장은 한 번에 하나 — 연타로 겹치면 한쪽 실패가 잠금을 풀어 버린다(Codex 재검수 3).
    if (movingRef.current) return;
    setError(null);
    // 🚨 옮기는 순간의 가사로 저장한다 — 저장을 기다리는 동안 초안을 고르면 그 초안은 저장되지 않고
    //    사라진다(Codex 재검수 P1). 학생이 내 가사로 넘어가기로 한 것이므로 제안을 거둔다.
    setOfferedDraft(null);
    movingRef.current = true;
    try {
      await autosave.flush({ ...payload, step });
      navigate(pathForStep(workId, step));
    } catch (caught) {
      movingRef.current = false;
      setError(caught instanceof ApiError ? caught.message : "저장하지 못했습니다.");
    }
  };

  return (
    <StudioShell
      currentStep={2}
      remainingSlots={initial.remaining_slots}
      dirty={dirty}
      autosaveStatus={autosave.status}
      notice={autosave.status === "error" ? "네트워크가 끊겼어요. 작성 내용은 이 기기에 보관하고 있어요." : undefined}
    >
      <div className="editor-split lyrics-layout">
        <aside className="editor-guide-panel">
          <h1 tabIndex={-1}>어떤 이야기로 노래할까요?</h1>
          <p>주제를 쓰고 초안을 받아서, 오른쪽에서 내 말로 고쳐요</p>
          <label className="field-label" htmlFor="song-topic">주제</label>
          <textarea
            id="song-topic"
            className="topic-textarea"
            ref={topicRef}
            rows={2}
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
            placeholder="예: 운동회에서 마지막 계주로 역전한 날"
            maxLength={200}
          />
          <CharCounter value={topic} max={200} />
          <button
            className="button-secondary full-width"
            type="button"
            onClick={() => void requestDraft()}
            disabled={drafting || !topic.trim()}
          >
            {drafting ? "초안 만드는 중..." : "가사 초안 받기"}
          </button>
          {provider ? <p className="provider-label">초안 제공: {provider}</p> : null}
          <div className="tip-card">
            <strong>좋은 가사 팁</strong>
            <ul>
              <li>그날의 장면 하나를 자세히 써보세요</li>
              <li>들리는 소리, 몸의 느낌을 넣으면 살아나요</li>
              <li>(후렴)은 짧고 따라 부르기 쉽게 써요</li>
              <li>(후렴) 같은 구조 표시가 노래의 뼈대를 잡아줘요</li>
            </ul>
          </div>
        </aside>
        <section className="editor-main-panel">
          <div className="field-heading-row">
            <label className="field-title" htmlFor="lyrics-body">내 가사</label>
            <span>초안을 받아도 여기서 마음껏 고쳐요 — 고친 만큼 내 노래가 돼요</span>
          </div>
          <textarea
            id="lyrics-body"
            className="lyrics-textarea"
            value={lyrics}
            onChange={(event) => setLyrics(event.target.value)}
            placeholder="내 이야기를 가사로 써보세요"
            maxLength={LYRICS_MAX_CHARS_LONG}
          />
          {/* 🔑 입력 상한은 2·3분 노래 기준(600자)이다. 길이는 다음 화면에서 고르므로 1분 기준(400자)도 같이 알린다. */}
          <CharCounter value={lyrics} max={LYRICS_MAX_CHARS_LONG} />
          {lyrics.length > LYRICS_MAX_CHARS ? (
            <p className="field-guidance">가사가 {LYRICS_MAX_CHARS}자를 넘었어요. 다음 화면에서 2분이나 3분 노래를 고르면 이 가사로 만들 수 있어요.</p>
          ) : null}
          {offeredDraft !== null ? (
            <div className="suggestion-area" role="status" aria-live="polite">
              <strong>AI 가 만든 가사가 도착했어요</strong>
              <p className="suggestion-hint">내가 쓴 가사는 그대로 있어요. AI 가사를 쓰면 내 가사가 모두 바뀌어요.</p>
              <p style={{ whiteSpace: "pre-wrap", maxHeight: "12rem", overflowY: "auto" }}>{offeredDraft}</p>
              <div className="button-row">
                <button
                  className="button-secondary"
                  type="button"
                  onClick={() => {
                    setLyrics(offeredDraft);
                    setOfferedDraft(null);
                  }}
                >
                  AI 가사 쓰기
                </button>
                <button className="button-ghost" type="button" onClick={() => setOfferedDraft(null)}>
                  내 가사 계속 쓰기
                </button>
              </div>
            </div>
          ) : null}
          {error ? <InlineError>{error}</InlineError> : null}
          <div className="editor-footer">
            <p>고운말 검사는 노래를 만들 때 자동으로 해요</p>
            <div className="button-row">
              <button className="button-secondary" type="button" onClick={() => void move("mode")}>이전</button>
              <button className="button-primary" type="button" disabled={!lyrics.trim()} onClick={() => void move("style")}>다음</button>
            </div>
          </div>
        </section>
      </div>
    </StudioShell>
  );
}
